// src/components/ReadhitComponent/BarChartComponent.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LabelList, ReferenceLine, Label,
} from 'recharts';
import _ from 'lodash';
import UserGuideBarChart from '../../utils/UserGuid/UserGuideBarChart.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const METRIC_LABELS = {
  pident: 'Percent Identity',
  bit:    'Bit Score',
  evalue: 'E-Value',
  len:    'Alignment Length',
  qlen:   'Query Length',
  gap:    'Gaps',
  mm:     'Mismatches',
};

const BAR_COLORS = [
  '#2563eb','#16a34a','#f59e0b','#ef4444','#8b5cf6',
  '#06b6d4','#ec4899','#84cc16','#f97316','#6366f1',
];

const MAX_ROWS    = 150000;
const PAGE_SIZES  = [5, 15, 25, 50];

const DEFAULTS = {
  metric:          'pident',
  xAxisField:      'poltype',
  secondaryMetric: 'count',
  itemsPerPage:    15,
  useAverage:      true,
  showLabels:      false,
  sortBy:          'value',
  sortDir:         'desc',
  filterValue:     '',
  refLine:         '',
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, fmtEvalue }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0',
      borderRadius: 6, padding: '10px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,.1)', fontSize: 12,
    }}>
      <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ margin: '2px 0', color: e.color }}>
          {e.name}: {e.name === 'evalue' ? fmtEvalue(e.value) : e.value}
        </p>
      ))}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function BarChartComponent({ data }) {
  const [guideOpen, setGuideOpen] = useState(false);
  const [metric,          setMetric]          = useState(DEFAULTS.metric);
  const [xAxisField,      setXAxisField]      = useState(DEFAULTS.xAxisField);
  const [secondaryMetric, setSecondaryMetric] = useState(DEFAULTS.secondaryMetric);
  const [useAverage,      setUseAverage]      = useState(DEFAULTS.useAverage);
  const [showLabels,      setShowLabels]      = useState(DEFAULTS.showLabels);
  const [sortBy,          setSortBy]          = useState(DEFAULTS.sortBy);
  const [sortDir,         setSortDir]         = useState(DEFAULTS.sortDir);
  const [filterValue,     setFilterValue]     = useState(DEFAULTS.filterValue);
  const [refLine,         setRefLine]         = useState(DEFAULTS.refLine);
  const [itemsPerPage,    setItemsPerPage]    = useState(DEFAULTS.itemsPerPage);
  const [page,            setPage]            = useState(1);
  const [warning,         setWarning]         = useState(false);

  const fmtEvalue = useCallback((v) => {
    if (v === 0) return '0';
    if (v < 0.0001) return v.toExponential(2);
    return v.toFixed(3);
  }, []);

  // ── Group raw data ──────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    if (!data?.length) return [];
    let src = data;
    if (src.length > MAX_ROWS) { setWarning(true); src = src.slice(0, MAX_ROWS); }
    else setWarning(false);

    const xKey = xAxisField === 'sseqid' ? 'sseqidPrefix' : xAxisField;

    const prepared = xAxisField === 'sseqid'
      ? src.map(item => {
          const parts = (item.sseqid ?? '').split('_');
          return { ...item, sseqidPrefix: parts.length >= 2 ? `${parts[0]}_${parts[1]}` : (item.sseqid ?? 'Unknown') };
        })
      : src;

    return _.chain(prepared)
      .groupBy(xKey)
      .map((grp, key) => {
        const metrics = {};
        Object.keys(METRIC_LABELS).forEach(k => {
          if (k === 'sseqid') return;
          const vals = grp.map(r => parseFloat(r[k] ?? 0)).filter(n => !isNaN(n));
          metrics[k] = vals.length
            ? parseFloat((useAverage ? _.mean(vals) : _.sum(vals)).toFixed(3))
            : 0;
        });
        return { [xKey]: key, count: grp.length, ...metrics };
      })
      .value();
  }, [data, xAxisField, useAverage]);

  // ── Filter + sort + paginate ────────────────────────────────────────────────
  const xKey = xAxisField === 'sseqid' ? 'sseqidPrefix' : xAxisField;

  const filtered = useMemo(() => {
    if (!filterValue.trim()) return grouped;
    const q = filterValue.toLowerCase();
    return grouped.filter(r => String(r[xKey] ?? '').toLowerCase().includes(q));
  }, [grouped, filterValue, xKey]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 'asc' : 'desc';
    if (sortBy === 'value') return _.orderBy(filtered, [metric], [dir]);
    if (sortBy === 'count') return _.orderBy(filtered, ['count'], [dir]);
    return _.orderBy(filtered, [xKey], [dir]);
  }, [filtered, sortBy, sortDir, metric, xKey]);

  const totalPages  = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const safePage    = Math.min(page, totalPages);
  const paginated   = useMemo(
    () => sorted.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [sorted, safePage, itemsPerPage]
  );

  useEffect(() => setPage(1), [filterValue, sortBy, sortDir, itemsPerPage, xAxisField, useAverage]);

  const reset = () => {
    setMetric(DEFAULTS.metric); setXAxisField(DEFAULTS.xAxisField);
    setSecondaryMetric(DEFAULTS.secondaryMetric); setUseAverage(DEFAULTS.useAverage);
    setShowLabels(DEFAULTS.showLabels); setSortBy(DEFAULTS.sortBy);
    setSortDir(DEFAULTS.sortDir); setFilterValue(DEFAULTS.filterValue);
    setRefLine(DEFAULTS.refLine); setItemsPerPage(DEFAULTS.itemsPerPage);
    setPage(1);
  };

  const fmtXTick = v => (typeof v === 'string' && v.length > 14) ? v.slice(0, 11) + '…' : v;
  const refVal   = refLine !== '' ? parseFloat(refLine) : null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="rh-chart-root">

      {/* ── Controls ── */}
      <div className="rh-chart-panel">
        <div className="rh-chart-panel-title">
          Metric Input
          <button className="rh-btn-reset" onClick={reset}>↺ Reset</button>
          <button className="rh-btn-reset" style={{ alignSelf: 'flex-end', marginBottom: 4 }}
            onClick={() => setGuideOpen(true)}>
              ? Guide
          </button>
        </div>

        <div className="rh-field">
          <label className="rh-label">X-Axis</label>
          <select className="rh-select" value={xAxisField}
            onChange={e => { setXAxisField(e.target.value); setPage(1); }}>
            <option value="poltype">Poly Type</option>
            <option value="sseqid">Sequence ID Prefix</option>
            <option value="qseqid">Query Sequence ID</option>
          </select>
        </div>

        <div className="rh-field">
          <label className="rh-label">Primary Metric</label>
          <select className="rh-select" value={metric} onChange={e => setMetric(e.target.value)}>
            {Object.entries(METRIC_LABELS).map(([k, v]) =>
              <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="rh-field">
          <label className="rh-label">Secondary Metric</label>
          <select className="rh-select" value={secondaryMetric}
            onChange={e => setSecondaryMetric(e.target.value)}>
            <option value="count">Count</option>
            {Object.entries(METRIC_LABELS)
              .filter(([k]) => k !== metric)
              .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <hr />

        <div className="rh-chart-panel-title">Display Options</div>

        <label className="rh-toggle-row">
          <span>Use Average (vs Sum)</span>
          <label className="rh-switch">
            <input type="checkbox" checked={useAverage} onChange={e => setUseAverage(e.target.checked)} />
            <span className="rh-switch-slider" />
          </label>
        </label>

        <label className="rh-toggle-row">
          <span>Show Value Labels</span>
          <label className="rh-switch">
            <input type="checkbox" checked={showLabels} onChange={e => setShowLabels(e.target.checked)} />
            <span className="rh-switch-slider" />
          </label>
        </label>

        <hr />

        <div className="rh-chart-panel-title">Sorting &amp; Filtering</div>

        <div className="rh-field">
          <label className="rh-label">Sort By</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select className="rh-select" style={{ flex: 1 }} value={sortBy}
              onChange={e => setSortBy(e.target.value)}>
              <option value="value">Metric Value</option>
              <option value="count">Count</option>
              <option value="label">Label</option>
            </select>
            <button className="rh-btn-reset" style={{ width: 32 }}
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              title="Toggle direction">
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        <div className="rh-field">
          <label className="rh-label">Filter</label>
          <input className="rh-input" value={filterValue}
            placeholder={`Filter by ${xAxisField}`}
            onChange={e => { setFilterValue(e.target.value); setPage(1); }} />
        </div>

        <hr />

        <div className="rh-field">
          <label className="rh-label">Items per page</label>
          <input type="range" className="rh-range" min={5} max={50} step={5}
            value={itemsPerPage}
            onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1); }} />
          <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', textAlign: 'center' }}>
            {itemsPerPage}
          </span>
        </div>

        <div className="rh-field">
          <label className="rh-label">Reference Line ({METRIC_LABELS[metric]})</label>
          <input type="number" className="rh-input" value={refLine}
            placeholder="Enter value"
            onChange={e => setRefLine(e.target.value)} />
        </div>

        {warning && (
          <div className="rh-alert rh-alert-warn">
            Dataset limited to first {MAX_ROWS.toLocaleString()} rows
          </div>
        )}
      </div>

      {/* ── Chart ── */}
      <div className="rh-chart-area">
        <div className="rh-chart-area-header">
          <div className="rh-chart-area-title">
            {METRIC_LABELS[metric]} {useAverage ? 'Average' : 'Sum'} by{' '}
            {xAxisField === 'sseqid' ? 'Sequence ID Prefix' : xAxisField === 'poltype' ? 'Poly Type' : 'Query Sequence ID'}
            {filterValue && ` (filtered: "${filterValue}")`}
          </div>
          <div className="rh-chart-area-subtitle">
            {sorted.length.toLocaleString()} groups
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="rh-chart-pagination">
            <button className="rh-page-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <span>Page {safePage} / {totalPages} ({sorted.length} total)</span>
            <button className="rh-page-btn" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        )}

        <div className="rh-chart-canvas">
          {paginated.length === 0 ? (
            <div className="rh-empty"><div className="rh-empty-icon">◎</div><span>No data</span></div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={paginated} margin={{ top: 20, right: 30, left: 30, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey={xKey} angle={-45} textAnchor="end" height={70}
                  interval={0} tick={{ fontSize: 11 }} tickFormatter={fmtXTick} />
                <YAxis tickFormatter={metric === 'evalue' ? fmtEvalue : undefined}
                  tick={{ fontSize: 11 }}
                  label={{ value: METRIC_LABELS[metric], angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }} />
                <Tooltip content={<ChartTooltip fmtEvalue={fmtEvalue} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {refVal !== null && (
                  <ReferenceLine y={refVal} stroke="#ef4444" strokeDasharray="4 3">
                    <Label value={String(refVal)} position="insideBottomRight" fill="#ef4444" fontSize={10} />
                  </ReferenceLine>
                )}
                <Bar dataKey={metric} fill={BAR_COLORS[0]} name={METRIC_LABELS[metric]}>
                  {showLabels && <LabelList dataKey={metric} position="top"
                    formatter={metric === 'evalue' ? fmtEvalue : v => v}
                    style={{ fontSize: 9 }} />}
                </Bar>
                <Bar dataKey={secondaryMetric} fill={BAR_COLORS[1]}
                  name={secondaryMetric === 'count' ? 'Count' : METRIC_LABELS[secondaryMetric]}>
                  {showLabels && <LabelList dataKey={secondaryMetric} position="top"
                    formatter={secondaryMetric === 'evalue' ? fmtEvalue : v => v}
                    style={{ fontSize: 9 }} />}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <UserGuideBarChart open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
export default React.memo(BarChartComponent);
